/**
 * @overview Handler file for inline callback (Fiserv 3DS2) for the OG Authorization actions.
 */

import { isNil, isNotNil } from '@flowio/is';
import isEqual from 'lodash/isEqual';
import { createExecuteScriptElement, createInlineWindowElements } from '../utilities/createActionDomElements';
import createInlineCallbackListener from '../utilities/createInlineCallbackListener';
import pollForAuthorizationUpdate from '../utilities/pollForAuthorizationUpdate';
import { removeElement } from '../utilities/elementHelpers';
import { hideLoadingSpinner, showLoadingSpinner } from '../utilities/loadingSpinner';

import type { PaymentRequestCallbackQuery } from '../types';
import fetchAuthorization from '../services/authorization/fetchAuthorization';

type InlineActionDetails = io.flow.v0.models.ThreedsIdentifyActionDetails
| io.flow.v0.models.ThreedsChallengeActionDetails;

type HandleOgAuthInlineActionParams = {
  authResultAction: io.flow.v0.models.AuthorizationResultActionNative,
  authKey: string,
  organizationId: string,
  sessionId: string,
  mount: HTMLElement,
  onComplete: (auth: io.flow.v0.models.AuthorizationResult) => void,
  onFailure: () => void,
};

function isThreedsIdentifyActionDetails(
  actionDetails: InlineActionDetails,
): actionDetails is io.flow.v0.models.ThreedsIdentifyActionDetails {
  return isNotNil((
    actionDetails as io.flow.v0.models.ThreedsIdentifyActionDetails
  ).threeds_identify_action);
}

export default function handleOgAuthInlineAction(
  params: HandleOgAuthInlineActionParams,
): Promise<void> {
  const authDetails = params.authResultAction.details;

  if (
    isNil(authDetails)
    || !(authDetails.discriminator === 'threeds_challenge_action_details'
    || authDetails.discriminator === 'threeds_identify_action_details')
  ) {
    throw new Error('handleOgAuthInlineAction: Unsupported AuthorizationResultAction provided.');
  }
  const callbackListener = createInlineCallbackListener<PaymentRequestCallbackQuery>({
    expiresAt: authDetails.expires_at,
  });
  const elements: HTMLElement[] = [];
  let cancelPolling: () => void;

  return fetchAuthorization({
    key: params.authKey,
    organizationId: params.organizationId,
    sessionId: params.sessionId,
  }).then((auth) => {
    if (isThreedsIdentifyActionDetails(authDetails)) {
      showLoadingSpinner({ container: params.mount });
      const iframe = createExecuteScriptElement({
        script: authDetails.threeds_identify_action.method,
      });
      elements.push(iframe);
      params.mount.appendChild(iframe);
    } else {
      const [iframe, form] = createInlineWindowElements({
        url: authDetails.threeds_challenge_action.acs_url,
        method: 'POST',
      });
      elements.push(iframe, form);

      const creqInput = document.createElement('input');
      creqInput.type = 'hidden';
      creqInput.name = 'creq';
      creqInput.value = authDetails.threeds_challenge_action.challenge_request;
      form.appendChild(creqInput);

      if (!isNil(authDetails.threeds_challenge_action.session_data)) {
        const sessionDataInput = document.createElement('input');
        sessionDataInput.type = 'hidden';
        sessionDataInput.name = 'sessionData';
        sessionDataInput.value = authDetails.threeds_challenge_action.session_data;
        form.appendChild(sessionDataInput);
      }
      params.mount.appendChild(iframe);
      params.mount.appendChild(form);
      form.submit();
    }

    const pollingPromise = new Promise<io.flow.v0.unions.Authorization>((resolve, reject) => {
      cancelPolling = pollForAuthorizationUpdate(
        {
          auth,
          organizationId: params.organizationId,
          sessionId: params.sessionId,
          diffCheck: (auth1, auth2) => !isEqual(auth1, auth2),
          onChange: (updatedAuth) => resolve(updatedAuth),
          onTimeout: reject,
        },
      );
    });

    return Promise.any([
      pollingPromise,
      callbackListener.then((callbackParams) => {
        if (isNil(callbackParams.authorization_key)) {
          // this will reject this promise, we still have a chance with the polling 🤞🏻
          throw new Error('handleDisplayInlineWindowAction: inline callback received without auth id');
        }
        cancelPolling();
        return fetchAuthorization({
          key: params.authKey,
          organizationId: params.organizationId,
          sessionId: params.sessionId,
        });
      }),
    ]).then((updatedAuth: io.flow.v0.unions.Authorization) => {
      hideLoadingSpinner();
      if (updatedAuth.result.action && updatedAuth.result.action.discriminator === 'authorization_result_action_native') {
        if (updatedAuth.result.action.details && updatedAuth.result.action.details.discriminator === 'threeds_challenge_action_details') {
          handleOgAuthInlineAction({
            ...params,
            authResultAction: updatedAuth.result.action,
          });
        }
      } else {
        params.onComplete(updatedAuth.result);
      }
    }).catch(() => {
      params.onFailure();
    }).finally(() => {
      elements.map(removeElement);
    });
  });
}
